import { createServerFn } from "@tanstack/react-start"
import { z } from "zod"

import {
  type AssessmentOutcome,
  liveAssessmentOutcomes,
} from "@/constants/terminal/assessment-outcome"
import { DetectionClass } from "@/constants/terminal/detection-class"
import { DetectionLifecycleState } from "@/constants/terminal/detection-lifecycle-state"
import { ResearchCategory } from "@/constants/terminal/research-category"
import { ResearchInsufficiencyReason } from "@/constants/terminal/research-insufficiency-reason"
import { ResearchSufficiencyOutcome } from "@/constants/terminal/research-sufficiency-outcome"
import type { TerminalDetectionCounts } from "@/lib/terminal/file-view"
import { briefText } from "@/terminal/file/brief-paragraphs"
import type { Brief } from "@/terminal/file/value-schemas"
import { readMonitoringRunUsage } from "@/terminal/monitoring/monitoring-usage"
import { readRunScope } from "@/terminal/monitoring/run-scope"

// The facts a Research Category cell renders, projected out of the stored
// Research Sufficiency by the query below and checked against their enums here.
const CategoryFactsSchema = z.array(
  z.object({
    category: z.nativeEnum(ResearchCategory),
    outcome: z.nativeEnum(ResearchSufficiencyOutcome),
    reasons: z.array(z.nativeEnum(ResearchInsufficiencyReason)),
    uncapturedCount: z.number().int().nonnegative(),
    uncovered: z.array(
      z.object({
        platformKey: z.string(),
        sourceKey: z.string(),
        subdivisionCodes: z.array(z.string()),
      })
    ),
  })
)

// A Company Run whose stored document no longer matches the schema is one
// unreadable row, not a blank console: the operator is here to watch the rest
// of the cohort, and a thrown parse would take every healthy run down with it.
const readCategories = (facts: unknown) => {
  if (facts === null)
    return { categories: null, isSufficiencyUnreadable: false }
  const parsed = CategoryFactsSchema.safeParse(facts)
  return parsed.success
    ? { categories: parsed.data, isSufficiencyUnreadable: false }
    : { categories: null, isSufficiencyUnreadable: true }
}

// Server-only modules, imported inside each handler so the client bundle never
// loads them. Every read is admin-only: a server function is directly
// callable, so the route guard is not enough.
const openConsole = async () => {
  const [
    { assertAdminContext },
    drizzle,
    { terminalCompany },
    { productionAssessmentByLegacyCompanyId },
    { terminalAssessment, terminalBrief, terminalDetection },
    { terminalCompanyRun, terminalMonitoringRun },
    { db },
    { portfolio },
    { isCurrentPortfolioEntry, terminalPortfolioEntry },
    { isCompanyRunFailed, monitoringRunState },
    { monitoringActivityColumns },
    { readRunLiveView },
    { revisionAsOf },
    { describePolicyVersions },
    { monitoringRunPolicyVersions },
  ] = await Promise.all([
    import("@/lib/supabase/auth"),
    import("drizzle-orm"),
    import("@/db/schema/terminal-company-identity"),
    import("@/admin/monitoring/production-assessments"),
    import("@/db/schema/terminal-file"),
    import("@/db/schema/terminal-schema"),
    import("@/lib/admin-db"),
    import("@/db/schema/public-terminal"),
    import("@/db/schema/terminal-portfolio"),
    import("@/terminal/monitoring/monitoring-operations"),
    import("@/admin/monitoring/monitoring-activity"),
    import("@/admin/monitoring/run-live-data"),
    import("@/terminal/file/live-parts"),
    import("@/terminal/policies/load-terminal-policy"),
    import("@/terminal/run-policy-versions"),
  ])
  await assertAdminContext()
  const { and, desc, eq, inArray, isNull, sql } = drizzle
  type Tx = Parameters<Parameters<typeof db.transaction>[0]>[0]

  const runColumns = {
    monitoringRunId: terminalMonitoringRun.id,
    policyVersions: monitoringRunPolicyVersions,
    codeRevision: terminalMonitoringRun.code_commit_sha,
    admissionDeadline: terminalMonitoringRun.admission_deadline,
    startedAt: terminalMonitoringRun.started_at,
    finishedAt: terminalMonitoringRun.finished_at,
    abandonedAt: terminalMonitoringRun.abandoned_at,
    plannedCompanyCount: sql<number>`cardinality(${terminalMonitoringRun.planned_company_ids})`,
    triggerMetadata: terminalMonitoringRun.trigger_metadata,
  }
  // Each Run's finished and failed Company counts, failure counted as the
  // Company table's phase reads it.
  const readRunCounts = async (tx: Tx, monitoringRunIds: string[]) => {
    const isFailed = isCompanyRunFailed(tx)
    const rows = monitoringRunIds.length
      ? await tx
          .select({
            monitoringRunId: terminalCompanyRun.monitoring_run_id,
            finishedCompanyCount:
              sql<number>`count(*) filter (where ${terminalCompanyRun.finished_at} is not null and not ${isFailed})`.mapWith(
                Number
              ),
            failedCompanyCount:
              sql<number>`count(*) filter (where ${isFailed})`.mapWith(Number),
          })
          .from(terminalCompanyRun)
          .where(
            inArray(terminalCompanyRun.monitoring_run_id, monitoringRunIds)
          )
          .groupBy(terminalCompanyRun.monitoring_run_id)
      : []
    return (monitoringRunId: string) => {
      const counts = rows.find((row) => row.monitoringRunId === monitoringRunId)
      return {
        finishedCompanyCount: counts?.finishedCompanyCount ?? 0,
        failedCompanyCount: counts?.failedCompanyCount ?? 0,
      }
    }
  }

  // A Run row with its scope read from the trigger metadata that stores it.
  const withScope = <Row extends { triggerMetadata: unknown }>({
    triggerMetadata,
    ...run
  }: Row) => ({ ...run, scope: readRunScope(triggerMetadata) })

  const readRun = async (tx: Tx, monitoringRunId: string) =>
    (
      await tx
        .select(runColumns)
        .from(terminalMonitoringRun)
        .where(eq(terminalMonitoringRun.id, monitoringRunId))
        .limit(1)
    ).map(withScope)[0]

  // The selected Run's Company table, usage and live Research view.
  const readSelectedRun = async (
    tx: Tx,
    run: NonNullable<Awaited<ReturnType<typeof readRun>>>,
    countsOf: Awaited<ReturnType<typeof readRunCounts>>
  ) => {
    const { monitoringRunId } = run
    const companyRuns = await tx
      .select({
        companyRunId: terminalCompanyRun.id,
        admittedAt: terminalCompanyRun.admitted_at,
        finishedAt: terminalCompanyRun.finished_at,
        stopReason: terminalCompanyRun.execution_summary,
        ...monitoringActivityColumns(tx),
        assessmentId: terminalAssessment.id,
        assessmentCompletedAt: terminalAssessment.completed_at,
        companyId: terminalCompany.id,
        displayName: terminalCompany.display_name,
        slug: terminalCompany.slug,
        outcome: terminalCompanyRun.outcome,
        exceptionReason: terminalCompanyRun.exception_reason,
        deferReason: terminalCompanyRun.defer_reason,
        attemptedReviewLabelCount:
          terminalCompanyRun.review_label_attempted_count,
        failedReviewLabelCount: terminalCompanyRun.review_label_failed_count,
        // A stored Research Sufficiency carries every requirement, search
        // subject and geography the evaluation walked, so a cohort-sized
        // Monitoring Run would haul hundreds of whole documents here to
        // discard nearly all of each one. The keys are the stored snake_case
        // spelling that research-sufficiency-storage writes.
        categoryFacts: sql<unknown>`(
          select jsonb_agg(jsonb_build_object(
            'category', sufficiency_category->>'category',
            'outcome', sufficiency_category->>'outcome',
            'reasons', sufficiency_category->'reasons',
            'uncapturedCount', coalesce(
              jsonb_array_length(sufficiency_category->'uncaptured_requirements'),
              0
            ),
            'uncovered', coalesce((
              select jsonb_agg(jsonb_build_object(
                'platformKey', requirement->>'platform_key',
                'sourceKey', requirement->>'source_key',
                'subdivisionCodes', coalesce(
                  requirement->'search_geography'->'subdivision_codes',
                  '[]'::jsonb
                )
              ))
              from jsonb_array_elements(
                sufficiency_category->'uncovered_requirements'
              ) as requirement
            ), '[]'::jsonb)
          ))
          from jsonb_array_elements(
            ${terminalCompanyRun.research_sufficiency}->'categories'
          ) as sufficiency_category
        )`,
        assessmentOutcome: terminalAssessment.outcome,
        assessmentErrorMessage: sql<
          string | null
        >`${terminalAssessment.error}->>'message'`,
        assessmentBand: terminalAssessment.band,
        assessmentScore: terminalAssessment.score,
        unclearReason: terminalAssessment.unclear_reason,
        legacyCompanyId: terminalCompany.legacy_company_id,
      })
      .from(terminalCompanyRun)
      .innerJoin(
        terminalCompany,
        eq(terminalCompany.id, terminalCompanyRun.company_id)
      )
      .leftJoin(
        terminalAssessment,
        eq(terminalAssessment.company_run_id, terminalCompanyRun.id)
      )
      .where(eq(terminalCompanyRun.monitoring_run_id, monitoringRunId))
      .orderBy(terminalCompany.display_name)

    // Each completed Assessment's active Detections by class, as the
    // buyer's Company list counts them, on its board as of the Assessment.
    const revision = revisionAsOf(tx, terminalDetection.id, {
      at: terminalAssessment.completed_at,
    })
    const classCounts = await tx
      .select({
        assessmentId: terminalAssessment.id,
        class: revision.class,
        count: sql<number>`count(*)`.mapWith(Number),
      })
      .from(terminalCompanyRun)
      .innerJoin(
        terminalAssessment,
        eq(terminalAssessment.company_run_id, terminalCompanyRun.id)
      )
      .innerJoin(
        terminalDetection,
        eq(terminalDetection.company_id, terminalAssessment.company_id)
      )
      .innerJoinLateral(revision, sql`true`)
      .where(
        and(
          eq(terminalCompanyRun.monitoring_run_id, monitoringRunId),
          inArray(terminalAssessment.outcome, liveAssessmentOutcomes),
          eq(revision.lifecycle_state, DetectionLifecycleState.ACTIVE)
        )
      )
      .groupBy(terminalAssessment.id, revision.class)
    const detectionCountsOf = (
      assessmentId: string | null,
      outcome: AssessmentOutcome | null
    ) =>
      liveAssessmentOutcomes.some((live) => live === outcome)
        ? (Object.fromEntries(
            Object.values(DetectionClass).map((detectionClass) => [
              detectionClass,
              classCounts.find(
                (row) =>
                  row.assessmentId === assessmentId &&
                  row.class === detectionClass
              )?.count ?? 0,
            ])
          ) as TerminalDetectionCounts)
        : null

    // A Portfolio that currently holds each Company, so the console can open
    // the Company's page as a buyer of that Portfolio sees it.
    const companyIds = companyRuns.map(({ companyId }) => companyId)
    const portfolioRows = companyIds.length
      ? await tx
          .select({
            companyId: terminalPortfolioEntry.company_id,
            portfolioSlug: portfolio.slug,
          })
          .from(terminalPortfolioEntry)
          .innerJoin(
            portfolio,
            eq(portfolio.id, terminalPortfolioEntry.portfolio_id)
          )
          .where(
            and(
              inArray(terminalPortfolioEntry.company_id, companyIds),
              isCurrentPortfolioEntry
            )
          )
      : []
    const portfolioSlugByCompanyId = new Map(
      portfolioRows.map(({ companyId, portfolioSlug }) => [
        companyId,
        portfolioSlug,
      ])
    )

    const { usage, usageOf } = await readMonitoringRunUsage(
      tx,
      companyRuns.map((row) => row.companyRunId)
    )
    const live = await readRunLiveView(tx, {
      monitoringRunId,
      policyVersions: run.policyVersions,
    })
    return {
      ...run,
      policyVersionsLabel: describePolicyVersions(run.policyVersions),
      ...countsOf(monitoringRunId),
      state: monitoringRunState(run),
      usage,
      live,
      companyRuns: companyRuns.map(
        ({ categoryFacts, legacyCompanyId, companyId, ...companyRun }) => ({
          ...companyRun,
          portfolioSlug: portfolioSlugByCompanyId.get(companyId) ?? null,
          productionPosture: legacyCompanyId
            ? (productionAssessmentByLegacyCompanyId[legacyCompanyId]
                ?.posture ?? null)
            : null,
          usage: usageOf(companyRun.companyRunId),
          detectionCounts: detectionCountsOf(
            companyRun.assessmentId,
            companyRun.assessmentOutcome
          ),
          ...readCategories(categoryFacts),
        })
      ),
    }
  }

  // The Brief written for the Company Run's Assessment as one text, and the
  // frozen production Brief.
  const readCompanyRunBriefs = async (tx: Tx, companyRunId: string) => {
    const [row] = await tx
      .select({
        legacyCompanyId: terminalCompany.legacy_company_id,
        claims: sql<Brief["claims"] | null>`${terminalBrief.brief}->'claims'`,
      })
      .from(terminalCompanyRun)
      .innerJoin(
        terminalCompany,
        eq(terminalCompany.id, terminalCompanyRun.company_id)
      )
      .leftJoin(
        terminalAssessment,
        eq(terminalAssessment.company_run_id, terminalCompanyRun.id)
      )
      .leftJoin(
        terminalBrief,
        eq(terminalBrief.assessment_id, terminalAssessment.id)
      )
      .where(eq(terminalCompanyRun.id, companyRunId))
      .limit(1)
    return {
      file: row?.claims ? briefText(row.claims) : null,
      production: row?.legacyCompanyId
        ? (productionAssessmentByLegacyCompanyId[row.legacyCompanyId]?.brief ??
          null)
        : null,
    }
  }

  const readOnly = <T>(read: (tx: Tx) => Promise<T>) =>
    db.transaction(read, {
      accessMode: "read only",
      isolationLevel: "repeatable read",
    })

  return {
    readOnly,
    readRun,
    readRunCounts,
    readSelectedRun,
    readCompanyRunBriefs,
    listRuns: async (tx: Tx, runLimit: number) =>
      (
        await tx
          .select(runColumns)
          .from(terminalMonitoringRun)
          .orderBy(desc(terminalMonitoringRun.started_at))
          .limit(runLimit)
      ).map(withScope),
    readActiveRunId: async (tx: Tx) =>
      (
        await tx
          .select({ id: terminalMonitoringRun.id })
          .from(terminalMonitoringRun)
          .where(
            and(
              isNull(terminalMonitoringRun.finished_at),
              isNull(terminalMonitoringRun.abandoned_at)
            )
          )
          .limit(1)
      )[0]?.id,
    monitoringRunState,
  }
}

const databaseTargetLabel = () => {
  const target = new URL(process.env.DATABASE_URL!)
  return `${target.hostname}:${target.port || "5432"}${target.pathname}`
}

// The console's read of Monitoring Runs: the recent Run list with its counts,
// and the selected Run, in one read-only transaction. Starting, abandoning and
// recovering a Run live in monitoring-launch-data.ts.
export const loadMonitoringConsole = createServerFn({ method: "GET" })
  .inputValidator(
    z
      .object({
        monitoringRunId: z.string().uuid().optional(),
        runLimit: z.number().int().min(50).max(1000).default(50),
      })
      .strict()
  )
  .handler(async ({ data }) => {
    const console = await openConsole()
    return console.readOnly(async (tx) => {
      const listedRuns = await console.listRuns(tx, data.runLimit)
      const countsOf = await console.readRunCounts(tx, [
        ...listedRuns.map((run) => run.monitoringRunId),
        ...(data.monitoringRunId ? [data.monitoringRunId] : []),
      ])
      const runs = listedRuns.map(({ finishedAt, abandonedAt, ...run }) => ({
        ...run,
        ...countsOf(run.monitoringRunId),
        state: console.monitoringRunState({ finishedAt, abandonedAt }),
      }))
      const monitoringRunId =
        data.monitoringRunId ??
        (await console.readActiveRunId(tx)) ??
        runs[0]?.monitoringRunId
      const selectedRun =
        monitoringRunId === undefined
          ? undefined
          : (listedRuns.find(
              (run) => run.monitoringRunId === monitoringRunId
            ) ?? (await console.readRun(tx, monitoringRunId)))
      return {
        runs,
        targetLabel: databaseTargetLabel(),
        refreshedAt: new Date().toISOString(),
        selected: selectedRun
          ? await console.readSelectedRun(tx, selectedRun, countsOf)
          : null,
      }
    })
  })

// The console's timer refresh: only the selected Run, without re-reading the
// Run list and its counts.
export const loadSelectedMonitoringRun = createServerFn({ method: "GET" })
  .inputValidator(z.object({ monitoringRunId: z.string().uuid() }).strict())
  .handler(async ({ data }) => {
    const console = await openConsole()
    return console.readOnly(async (tx) => {
      const run = await console.readRun(tx, data.monitoringRunId)
      return {
        refreshedAt: new Date().toISOString(),
        selected: run
          ? await console.readSelectedRun(
              tx,
              run,
              await console.readRunCounts(tx, [data.monitoringRunId])
            )
          : null,
      }
    })
  })

// The two Briefs behind a Company Run's posture cells, read when the operator
// first hovers one. Kept out of the table read, which the console polls,
// because a cohort's Briefs run to a megabyte.
export const loadCompanyRunBriefs = createServerFn({ method: "GET" })
  .inputValidator(z.object({ companyRunId: z.string().uuid() }).strict())
  .handler(async ({ data }) => {
    const console = await openConsole()
    return console.readOnly((tx) =>
      console.readCompanyRunBriefs(tx, data.companyRunId)
    )
  })

export type CompanyRunBriefs = Awaited<ReturnType<typeof loadCompanyRunBriefs>>

export type MonitoringConsoleData = Awaited<
  ReturnType<typeof loadMonitoringConsole>
>
export type SelectedRun = NonNullable<MonitoringConsoleData["selected"]>
export type CompanyRunRow = SelectedRun["companyRuns"][number]
